import React, { useCallback, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import StickyNote2Rounded from "@mui/icons-material/StickyNote2Rounded";
import CakeRounded from "@mui/icons-material/CakeRounded";
import PhoneRounded from "@mui/icons-material/PhoneRounded";
import EmailRounded from "@mui/icons-material/EmailRounded";
import CameraAltRounded from "@mui/icons-material/CameraAltRounded";
import TextField from "../../TextField";
import User from "../../../api/User";
import { useCookies } from "react-cookie";
import dayjs from "dayjs";
import BirthdayField from "../../BirthdayField";
import BottomButton from "../../BottomButton";
import { CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";

interface Props {
    user: User;
    onSaveNote: (note: string) => void;
}

const ClientDetails = (props: Props) => {
    const { user, onSaveNote } = props;
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const parseBirthday = (birth: string): string => {
        const parsedDate = dayjs(birth, "YYYY-MM-DD", true);
        if (parsedDate.isValid()) {
            return parsedDate.format("DD.MM");
        } else {
            return "";
        }
    };

    const [email, setEmail] = useState(user.email);
    const [birth, setBirth] = useState(() => parseBirthday(user.birth));
    const [phone, setPhone] = useState(user.phone);
    const [instagram, setInstagram] = useState(user.instagram);
    const [note, setNote] = useState(user.note);

    const [loading, setLoading] = useState(false);

    const onSaveNoteChanges = useCallback(() => {
        if (!uuid || !brand_id) return;
        User.updateNote(uuid, brand_id, user.key, note)
            .then(() => onSaveNote(note))
            .catch(console.error);
    }, [uuid, brand_id, note, user, onSaveNote]);

    const onSaveChanges = useCallback(() => {
        if (!uuid || !brand_id) return;

        const isValidBirthday = birth.match(/^\d{2}\.\d{2}$/);
        if (birth && !isValidBirthday) {
            alert("Please enter a valid birthday before registering.");
            return;
        }

        setLoading(true);

        const convertBirthdayToDate = (
            birthday: string
        ): string | undefined => {
            const [day, month] = birthday.split(".");
            if (day && month) {
                return `1970-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            }
            return undefined;
        };
        const formattedBirthday = birth
            ? convertBirthdayToDate(birth)
            : undefined;

        User.updateDetails(
            uuid,
            brand_id,
            user.key,
            phone,
            instagram,
            formattedBirthday
        )
            .then()
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, brand_id, user.key, birth, phone, instagram]);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    return (
        <Stack sx={{ px: 2, pt: 0.5, pb: 3 }}>
            {/* Notes section */}
            <Typography
                fontSize="11px"
                fontWeight={600}
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing="0.5px"
                sx={{ mb: 1.5 }}
            >
                Notes
            </Typography>
            <Stack sx={CARD_STYLES} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={iconBoxSx}>
                        <StickyNote2Rounded
                            sx={{ fontSize: 18, color: primary }}
                        />
                    </Box>
                    <Typography sx={FORM_LABEL_SX}>Client note</Typography>
                </Stack>
                <TextField
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    placeholder="Add a note about this client..."
                    fullWidth
                    multiline
                    variant="contained"
                    sx={{
                        bgcolor: "transparent",
                        fontWeight: 400,
                        fontSize: "15px",
                    }}
                />
                <BottomButton
                    onClick={onSaveNoteChanges}
                    variant="medium"
                    width="100%"
                    noLoading
                    sx={{
                        p: 0,
                        mb: "0 !important",
                        width: "100%",
                    }}
                >
                    Save note
                </BottomButton>
            </Stack>

            {/* Personal info section */}
            <Typography
                fontSize="11px"
                fontWeight={600}
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing="0.5px"
                sx={{ mt: 3, mb: 1.5 }}
            >
                Personal info
            </Typography>
            <Stack sx={CARD_STYLES} spacing={2.5}>
                {/* Birthday */}
                <Stack spacing={0.75}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Box sx={iconBoxSx}>
                            <CakeRounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography sx={FORM_LABEL_SX}>Birthday</Typography>
                    </Stack>
                    <BirthdayField
                        value={birth || ""}
                        onChange={(newVal: string) => setBirth(newVal)}
                    />
                </Stack>

                {/* Phone */}
                <Stack spacing={0.75}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Box sx={iconBoxSx}>
                            <PhoneRounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography sx={FORM_LABEL_SX}>Phone</Typography>
                    </Stack>
                    <TextField
                        value={phone}
                        onChange={(e) => setPhone(e.currentTarget.value)}
                        variant="underlined"
                    />
                </Stack>

                {/* E-Mail */}
                <Stack spacing={0.75}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Box sx={iconBoxSx}>
                            <EmailRounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography sx={FORM_LABEL_SX}>E-Mail</Typography>
                    </Stack>
                    <TextField
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        variant="underlined"
                        disabled
                    />
                </Stack>

                {/* Instagram */}
                <Stack spacing={0.75}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Box sx={iconBoxSx}>
                            <CameraAltRounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography sx={FORM_LABEL_SX}>Instagram</Typography>
                    </Stack>
                    <TextField
                        value={instagram}
                        onChange={(e) => setInstagram(e.currentTarget.value)}
                        variant="underlined"
                    />
                </Stack>
            </Stack>

            <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                <BottomButton disabled={loading} onClick={() => onSaveChanges()}>
                    Save Changes
                </BottomButton>
            </Box>
        </Stack>
    );
};

export default ClientDetails;
